import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PACKAGES, CONTACT, PACKAGE_ID_BY_DAYS } from "../data";
import { Send, AlertCircle, Upload, Check, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingForm = ({ selectedPackage, setSelectedPackage, selectedDate, setSelectedDate, selectedDays, setSelectedDays, selectedCabin, setSelectedCabin, selectedDateKey, onAvailabilityChanged }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    travel_date: "",
    pax: 2,
    dive_level: "",
    dietary_restriction: "",
    has_paid: "",
    paid_divers: "",
    paid_companions: "",
    birth_date: "",
    cpf: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPath, setReceiptPath] = useState("");
  const [uploading, setUploading] = useState(false);

  // If user picked a calendar date → show only Com/Sem mergulho for matched duration
  const matchedPkg = selectedDays ? PACKAGES.find((p) => p.id === PACKAGE_ID_BY_DAYS[selectedDays]) : null;
  const packageOptions = matchedPkg
    ? [
        `${matchedPkg.duration} · ${matchedPkg.nights} · Com Mergulho`,
        `${matchedPkg.duration} · ${matchedPkg.nights} · Sem Mergulho`,
      ]
    : PACKAGES.flatMap((p) => [
        `${p.duration} · ${p.nights} · Com Mergulho`,
        `${p.duration} · ${p.nights} · Sem Mergulho`,
      ]);

  const isDivePackage = selectedPackage.includes("Com Mergulho");

  const DIVE_LEVELS = [
    "Open Water",
    "Avançado",
    "Outros",
  ];

  const handleReceiptChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Arquivo muito grande. Máximo 8 MB.");
      return;
    }
    setReceiptFile(file);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/uploads/receipt`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReceiptPath(res.data.path);
      toast.success("Comprovante enviado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao enviar o arquivo");
      setReceiptFile(null);
    } finally {
      setUploading(false);
    }
  };

  const clearReceipt = () => {
    setReceiptFile(null);
    setReceiptPath("");
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !selectedPackage) {
      toast.error("Preencha nome, telefone e selecione um pacote.");
      return;
    }
    if (!selectedCabin) {
      toast.error("Selecione a cabine desejada.");
      return;
    }
    if (!form.dietary_restriction.trim()) {
      toast.error("Informe sua restrição alimentar (ou 'Nenhuma').");
      return;
    }
    if (isDivePackage && !form.dive_level) {
      toast.error("Selecione seu nível de certificação de mergulho.");
      return;
    }
    if (form.has_paid === "sim" && !receiptPath) {
      toast.error("Anexe o comprovante de pagamento.");
      return;
    }
    if (form.has_paid === "sim") {
      const total = parseInt(form.pax) || 0;
      const d = parseInt(form.paid_divers) || 0;
      const c = parseInt(form.paid_companions) || 0;
      if (d + c !== total) {
        toast.error(`Mergulhadores (${d}) + Acompanhantes (${c}) precisa somar ${total} pessoa(s).`);
        return;
      }
      if (!form.birth_date) {
        toast.error("Informe a data de nascimento.");
        return;
      }
      if (!form.cpf.trim()) {
        toast.error("Informe o CPF.");
        return;
      }
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/reservations`, {
        ...form,
        pax: parseInt(form.pax) || 1,
        package: selectedPackage,
        travel_date: selectedDate || form.travel_date,
        cabin: selectedCabin || "",
        date_key: selectedDateKey || "",
        dive_level: isDivePackage ? form.dive_level : "",
        has_paid: form.has_paid === "sim" ? true : form.has_paid === "nao" ? false : null,
        receipt_path: form.has_paid === "sim" ? receiptPath : "",
        paid_divers: form.has_paid === "sim" ? parseInt(form.paid_divers) || 0 : null,
        paid_companions: form.has_paid === "sim" ? parseInt(form.paid_companions) || 0 : null,
        birth_date: form.has_paid === "sim" ? form.birth_date : "",
        cpf: form.has_paid === "sim" ? form.cpf : "",
      });
      if (res.data?.auto_approved) {
        toast.success("Pré-reserva confirmada — cabine reservada! Abrindo WhatsApp para combinar detalhes...", { duration: 5000 });
        if (onAvailabilityChanged) onAvailabilityChanged();
      } else if (res.data?.approve_error) {
        toast.warning(`Pré-reserva registrada, mas ${res.data.approve_error} Vamos confirmar por WhatsApp.`, { duration: 6000 });
      } else {
        toast.success("Pré-reserva registrada! Abrindo WhatsApp...");
      }
      setTimeout(() => {
        window.open(res.data.whatsapp_url, "_blank");
      }, 600);
    } catch (err) {
      toast.error("Não foi possível enviar. Tente o WhatsApp direto.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="reserva" className="relative py-24 md:py-32 overflow-hidden" data-testid="booking-section">
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div
          className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(0,168,232,0.4), transparent 70%)" }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="overline mb-4">Reserve sua saída</div>
            <h2 className="font-display text-4xl md:text-5xl font-light leading-tight">
              Sua aventura começa
              <span className="block" style={{ color: "var(--ocean-accent)" }}>com uma mensagem.</span>
            </h2>
            <p className="mt-6 text-base leading-relaxed" style={{ color: "var(--ocean-muted)" }}>
              Preencha o formulário e nós te chamamos no WhatsApp com a confirmação,
              opções de datas disponíveis e o passo-a-passo do pagamento.
            </p>

            <div className="mt-10 glass p-6">
              <div className="overline mb-3" style={{ color: "var(--ocean-accent)" }}>Fale direto com</div>
              <div className="font-display text-2xl">{CONTACT.name}</div>
              <div className="text-sm mt-1" style={{ color: "var(--ocean-muted)" }}>
                APECATU Expedições · {CONTACT.city}
              </div>
              <div className="mt-4 text-base">{CONTACT.phone}</div>
              <a href={`mailto:${CONTACT.email}`} className="text-sm mt-1 inline-block underline" style={{ color: "var(--ocean-primary)" }} data-testid="booking-email-link">
                {CONTACT.email}
              </a>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="md:col-span-7 glass p-8 md:p-10 space-y-5" data-testid="booking-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Nome completo *" name="name" value={form.name} onChange={handleChange} testid="input-name" />
              <Field label="Telefone / WhatsApp *" name="phone" value={form.phone} onChange={handleChange} testid="input-phone" />
              <Field label="E-mail" name="email" type="email" value={form.email} onChange={handleChange} testid="input-email" />
              {selectedDate ? (
                <div>
                  <label className="overline block mb-2">Data selecionada</label>
                  <div
                    className="w-full px-4 py-3 text-sm flex items-center justify-between"
                    style={{ background: "rgba(245,199,84,0.10)", border: "1px solid rgba(245,199,84,0.40)", color: "var(--ocean-accent)" }}
                    data-testid="selected-date-display"
                  >
                    <span>{selectedDate}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedDate("");
                        if (setSelectedDays) setSelectedDays(null);
                        setSelectedPackage("");
                      }}
                      className="text-xs underline"
                      style={{ color: "var(--ocean-muted)" }}
                    >
                      alterar
                    </button>
                  </div>
                </div>
              ) : (
                <Field label="Data prevista" name="travel_date" type="date" value={form.travel_date} onChange={handleChange} testid="input-date" />
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="overline block mb-2">
                  {matchedPkg ? `Pacote · ${matchedPkg.duration} *` : "Pacote desejado *"}
                </label>
                <select
                  className="w-full px-4 py-3 text-sm focus:outline-none"
                  style={{ background: "var(--ocean-surface)", border: "1px solid var(--ocean-border)", color: "#fff" }}
                  value={selectedPackage}
                  onChange={(e) => setSelectedPackage(e.target.value)}
                  data-testid="select-package"
                >
                  <option value="">
                    {matchedPkg ? "Com ou sem mergulho?" : "Selecione..."}
                  </option>
                  {packageOptions.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
                {matchedPkg && (
                  <div className="text-[0.65rem] mt-1.5" style={{ color: "var(--ocean-muted)" }}>
                    Duração definida pela data selecionada ({matchedPkg.duration} · {matchedPkg.nights})
                  </div>
                )}
              </div>
              <div>
                <label className="overline block mb-2">Cabine desejada *</label>
                <select
                  className="w-full px-4 py-3 text-sm focus:outline-none"
                  style={{ background: "var(--ocean-surface)", border: "1px solid var(--ocean-border)", color: "#fff" }}
                  value={selectedCabin || ""}
                  onChange={(e) => setSelectedCabin && setSelectedCabin(e.target.value)}
                  data-testid="select-cabin"
                >
                  <option value="">Selecione...</option>
                  <option value="Cabine Casal">Cabine Casal (2 pessoas · casal)</option>
                  <option value="Suíte Tripla">Suíte Tripla (3 pessoas · compartilhada)</option>
                  <option value="Suíte Casal">Suíte Casal (2 pessoas · casal)</option>
                  <option value="Cabine Quádrupla · Lado A">Cabine Quádrupla · Lado A (2 pessoas · compartilhada)</option>
                  <option value="Cabine Quádrupla · Lado B">Cabine Quádrupla · Lado B (2 pessoas · compartilhada)</option>
                </select>
                <div className="text-[0.65rem] mt-1.5" style={{ color: "var(--ocean-muted)" }}>
                  Cabines compartilhadas podem ter restrição de gênero. Confirmaremos disponibilidade no WhatsApp.
                </div>
              </div>
              <Field label="Quantidade de pessoas" name="pax" type="number" min={1} value={form.pax} onChange={handleChange} testid="input-pax" />
            </div>

            {isDivePackage && (
              <div>
                <label className="overline block mb-2">
                  Nível de certificação de mergulho *
                </label>
                <select
                  name="dive_level"
                  value={form.dive_level}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm focus:outline-none"
                  style={{ background: "var(--ocean-surface)", border: "1px solid var(--ocean-border)", color: "#fff" }}
                  data-testid="select-dive-level"
                >
                  <option value="">Selecione...</option>
                  {DIVE_LEVELS.map((lvl) => (
                    <option key={lvl} value={lvl}>{lvl}</option>
                  ))}
                </select>
                <div className="text-[0.65rem] mt-1.5" style={{ color: "var(--ocean-muted)" }}>
                  Ajuda a organizar duplas e definir se você pode fazer mergulhos noturnos (exigem Avançado).
                </div>
              </div>
            )}

            {/* Restrição alimentar — obrigatório */}
            <div>
              <label className="overline block mb-2">
                Restrição alimentar *
              </label>
              <input
                name="dietary_restriction"
                type="text"
                value={form.dietary_restriction}
                onChange={handleChange}
                placeholder="Ex.: Vegetariano, alergia a frutos do mar, ou 'Nenhuma'"
                className="w-full px-4 py-3 text-sm focus:outline-none"
                style={{ background: "var(--ocean-surface)", border: "1px solid var(--ocean-border)", color: "#fff" }}
                data-testid="input-dietary"
              />
            </div>

            {/* Já fez pagamento? */}
            <div>
              <label className="overline block mb-2">
                Já efetuou o pagamento?
              </label>
              <div className="flex gap-3">
                {[
                  { val: "sim", label: "Sim" },
                  { val: "nao", label: "Ainda não" },
                ].map((opt) => {
                  const active = form.has_paid === opt.val;
                  return (
                    <button
                      key={opt.val}
                      type="button"
                      onClick={() => setForm({ ...form, has_paid: opt.val })}
                      className="flex-1 px-4 py-3 text-xs uppercase tracking-wider font-bold transition"
                      style={{
                        background: active ? "var(--ocean-accent)" : "var(--ocean-surface)",
                        color: active ? "#04111E" : "#fff",
                        border: `1px solid ${active ? "var(--ocean-accent)" : "var(--ocean-border)"}`,
                      }}
                      data-testid={`has-paid-${opt.val}`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {form.has_paid === "sim" && (
                <div className="mt-3 space-y-3">
                  {/* Composição do grupo pago */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="overline block mb-2">Mergulhadores *</label>
                      <input
                        name="paid_divers"
                        type="number"
                        min={0}
                        value={form.paid_divers}
                        onChange={handleChange}
                        placeholder="0"
                        className="w-full px-3 py-3 text-sm focus:outline-none"
                        style={{ background: "var(--ocean-surface)", border: "1px solid var(--ocean-border)", color: "#fff" }}
                        data-testid="input-paid-divers"
                      />
                    </div>
                    <div>
                      <label className="overline block mb-2">Acompanhantes *</label>
                      <input
                        name="paid_companions"
                        type="number"
                        min={0}
                        value={form.paid_companions}
                        onChange={handleChange}
                        placeholder="0"
                        className="w-full px-3 py-3 text-sm focus:outline-none"
                        style={{ background: "var(--ocean-surface)", border: "1px solid var(--ocean-border)", color: "#fff" }}
                        data-testid="input-paid-companions"
                      />
                    </div>
                  </div>
                  <div className="text-[0.65rem]" style={{ color: "var(--ocean-muted)" }}>
                    A soma precisa bater com <strong>{form.pax || 0}</strong> pessoa(s) do campo acima.
                  </div>

                  {/* Dados fiscais para emissão de nota / registro */}
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="overline block mb-2">Data de nascimento *</label>
                      <input
                        name="birth_date"
                        type="date"
                        value={form.birth_date}
                        onChange={handleChange}
                        className="w-full px-3 py-3 text-sm focus:outline-none"
                        style={{ background: "var(--ocean-surface)", border: "1px solid var(--ocean-border)", color: "#fff" }}
                        data-testid="input-birth-date"
                      />
                    </div>
                    <div>
                      <label className="overline block mb-2">CPF *</label>
                      <input
                        name="cpf"
                        type="text"
                        inputMode="numeric"
                        value={form.cpf}
                        onChange={handleChange}
                        placeholder="000.000.000-00"
                        className="w-full px-3 py-3 text-sm focus:outline-none"
                        style={{ background: "var(--ocean-surface)", border: "1px solid var(--ocean-border)", color: "#fff" }}
                        data-testid="input-cpf"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="overline block mb-2">Anexar comprovante *</label>
                  {!receiptFile ? (
                    <label
                      className="flex items-center gap-3 px-4 py-3 cursor-pointer transition hover:bg-white/5"
                      style={{ background: "var(--ocean-surface)", border: "1px dashed var(--ocean-border)", color: "var(--ocean-muted)" }}
                      data-testid="receipt-upload-label"
                    >
                      <Upload size={16} />
                      <span className="text-sm">Selecionar imagem ou PDF (máx 8 MB)</span>
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={handleReceiptChange}
                        data-testid="receipt-file-input"
                      />
                    </label>
                  ) : (
                    <div
                      className="flex items-center justify-between gap-3 px-4 py-3"
                      style={{ background: "rgba(31,168,102,0.12)", border: "1px solid rgba(31,168,102,0.4)" }}
                      data-testid="receipt-attached"
                    >
                      <div className="flex items-center gap-2 text-sm text-white min-w-0">
                        {uploading ? (
                          <span className="text-xs" style={{ color: "var(--ocean-accent)" }}>Enviando...</span>
                        ) : (
                          <>
                            <Check size={16} style={{ color: "#1FA866" }} />
                            <span className="truncate">{receiptFile.name}</span>
                          </>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={clearReceipt}
                        className="p-1.5 hover:bg-white/10"
                        data-testid="receipt-clear"
                        aria-label="Remover comprovante"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  )}
                </div>
                </div>
              )}
            </div>

            <div>
              <label className="overline block mb-2">Mensagem (opcional)</label>
              <textarea
                name="message"
                rows={4}
                value={form.message}
                onChange={handleChange}
                placeholder="Conte um pouco sobre o que você procura... mergulhador iniciante, certificações, alergias, datas flexíveis..."
                className="w-full px-4 py-3 text-sm resize-none focus:outline-none"
                style={{ background: "var(--ocean-surface)", border: "1px solid var(--ocean-border)", color: "#fff" }}
                data-testid="input-message"
              />
            </div>

            <div className="flex items-start gap-2 text-xs" style={{ color: "var(--ocean-muted)" }}>
              <AlertCircle size={14} style={{ marginTop: 2 }} />
              <span>
                Isso é uma <strong style={{ color: "var(--ocean-accent)" }}>pré-reserva</strong>.
                A vaga só é confirmada após aprovação do pagamento. Você será redirecionado ao WhatsApp
                do Lucas com sua mensagem pronta.
              </span>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center text-base py-4"
              data-testid="submit-booking-btn"
            >
              <Send size={16} /> {loading ? "Enviando..." : "Enviar pré-reserva via WhatsApp"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testid, ...props }) => (
  <div>
    <label className="overline block mb-2">{label}</label>
    <input
      {...props}
      data-testid={testid}
      className="w-full px-4 py-3 text-sm focus:outline-none transition"
      style={{
        background: "var(--ocean-surface)",
        border: "1px solid var(--ocean-border)",
        color: "#fff",
      }}
    />
  </div>
);

export default BookingForm;
